import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import rushLogo from './rush-logo.png';
import './rush.css';
import './auth.css';
import { loginSuccess } from '../../slices/authSlice';
import { storeAuthToken } from '../../utils/authToken';

// Enrolment page: fetch a fresh secret + QR, the user scans it, enters a
// code, and we flip twoFactorEnabled. Reachable two ways:
//   1) /two-factor/setup — user is already authenticated, opt-in from settings
//   2) /two-factor/setup?ct=… — login returned needs2FA: 'enroll' because the
//      grace period expired; we use the challenge token to enroll then sign in.
const RushTwoFactorSetupPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const authToken = useSelector((s) => s.auth?.token);

    const challengeToken = useMemo(
        () => new URLSearchParams(location.search).get('ct') || '',
        [location.search],
    );

    const [secret, setSecret] = useState('');
    const [qrDataUrl, setQrDataUrl] = useState('');
    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [backupCodes, setBackupCodes] = useState([]);
    const [acknowledged, setAcknowledged] = useState(false);

    // For the enrollment-enforced flow we don't have an auth JWT yet, so the
    // backend's /setup endpoint (which needs auth) won't work directly. We
    // mint a temporary auth JWT by exchanging the challenge token via a side
    // endpoint — but we kept it simple: enforce that this page is opened
    // either by an authenticated session OR by a freshly-issued challenge
    // token that the backend will accept as proof of password-step success.
    const bearer = authToken;

    useEffect(() => {
        let cancelled = false;
        const fetchSetup = async () => {
            try {
                const res = await axios.post(
                    '/api/2fa/setup',
                    { challengeToken },
                    { headers: bearer ? { Authorization: `Bearer ${bearer}` } : {} },
                );
                if (cancelled) return;
                setSecret(res.data.secret);
                setQrDataUrl(res.data.qrDataUrl);
            } catch (err) {
                if (cancelled) return;
                setError(err.response?.data?.msg || 'Could not start 2FA setup.');
            }
        };
        if (bearer || challengeToken) fetchSetup();
        return () => { cancelled = true; };
    }, [bearer, challengeToken]);

    const handleVerify = async (e) => {
        e.preventDefault();
        setError('');
        if (code.length !== 6) {
            setError('Enter the 6-digit code from your authenticator.');
            return;
        }
        setSubmitting(true);
        try {
            const res = await axios.post(
                '/api/2fa/verify-setup',
                { code, challengeToken },
                { headers: bearer ? { Authorization: `Bearer ${bearer}` } : {} },
            );
            setDone(true);
            setBackupCodes(Array.isArray(res.data.backupCodes) ? res.data.backupCodes : []);

            // Enrolment-enforced flow returns a fresh session token. The
            // opt-in flow (already signed in) reuses the existing JWT.
            if (!bearer && res.data.token) {
                const newToken = res.data.token;
                storeAuthToken(newToken, true);
                try {
                    const me = await axios.get('/api/users/me', {
                        headers: { Authorization: `Bearer ${newToken}` },
                    });
                    dispatch(loginSuccess({ token: newToken, user: me.data }));
                } catch (_) { /* fall through to login screen */ }
            }
            // We deliberately do NOT auto-redirect here — the user must
            // acknowledge the backup codes first. Navigation happens in the
            // "I've saved my codes" handler below.
        } catch (err) {
            setError(err.response?.data?.msg || 'Verification failed.');
            setSubmitting(false);
        }
    };

    if (!bearer && !challengeToken) {
        return (
            <div className="rs auth-page">
                <section className="auth-form-panel">
                    <div className="auth-form-body">
                        <h1 className="auth-title">Sign in first</h1>
                        <p className="auth-sub">
                            Please <Link to="/login">sign in</Link> to set up two-factor authentication.
                        </p>
                    </div>
                </section>
            </div>
        );
    }

    return (
        <div className="rs auth-page">
            <section className="auth-form-panel">
                <header className="auth-form-header">
                    <Link to="/" className="rs-logo rs-logo-img">
                        <img src={rushLogo} alt="Rush Studio" />
                    </Link>
                </header>
                <div className="auth-form-body">
                    <div className="auth-eyebrow">Two-factor authentication</div>
                    <h1 className="auth-title">Add an authenticator.</h1>
                    <p className="auth-sub">
                        Rush Studio uses TOTP — same standard as Google Authenticator, Authy,
                        and 1Password. Scan the QR with your app, then enter the 6-digit code
                        it shows.
                    </p>

                    {error && <div className="auth-error">{error}</div>}

                    {qrDataUrl ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, margin: '12px 0 20px' }}>
                            <img src={qrDataUrl} alt="2FA QR" width={200} height={200} style={{ borderRadius: 8, background: '#fff', padding: 8 }} />
                            <details style={{ fontSize: 13, color: '#888' }}>
                                <summary>Can't scan? Show secret</summary>
                                <code style={{ display: 'block', marginTop: 6, fontSize: 13, wordBreak: 'break-all' }}>{secret}</code>
                            </details>
                        </div>
                    ) : (
                        !error && <div className="auth-sub">Generating your secret…</div>
                    )}

                    {done ? (
                        <div className="auth-success" style={{ display: 'block' }}>
                            <div style={{ fontWeight: 600, marginBottom: 8 }}>
                                Two-factor authentication is on.
                            </div>
                            <p className="auth-sub" style={{ margin: '8px 0 12px' }}>
                                Save these <b>backup codes</b> somewhere safe — a password manager,
                                printed paper, or both. If you lose your authenticator app, each
                                code lets you sign in once. We can't show them again.
                            </p>
                            <pre
                                style={{
                                    background: '#0e0e10',
                                    color: '#fbfaf7',
                                    padding: 16,
                                    borderRadius: 8,
                                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                    fontSize: 14,
                                    lineHeight: 1.7,
                                    overflowX: 'auto',
                                    margin: '0 0 12px',
                                }}
                            >{backupCodes.join('\n')}</pre>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                                <button
                                    type="button"
                                    className="btn btn-ghost btn-sm"
                                    onClick={() => navigator.clipboard?.writeText(backupCodes.join('\n'))}
                                >
                                    Copy
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-ghost btn-sm"
                                    onClick={() => {
                                        const blob = new Blob(
                                            [`Rush Studio — backup codes\n\n${backupCodes.join('\n')}\n`],
                                            { type: 'text/plain' },
                                        );
                                        const url = URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = url;
                                        a.download = 'rush-studio-backup-codes.txt';
                                        a.click();
                                        URL.revokeObjectURL(url);
                                    }}
                                >
                                    Download .txt
                                </button>
                            </div>
                            <label className="checkbox-row" style={{ marginBottom: 12 }}>
                                <input
                                    type="checkbox"
                                    checked={acknowledged}
                                    onChange={(e) => setAcknowledged(e.target.checked)}
                                />
                                <span>I've saved these codes somewhere I can find them.</span>
                            </label>
                            <button
                                type="button"
                                className="btn btn-accent auth-submit"
                                disabled={!acknowledged}
                                onClick={() => navigate(bearer ? '/dashboard/settings' : '/dashboard', { replace: true })}
                            >
                                Continue
                            </button>
                        </div>
                    ) : (
                        <form className="auth-form" onSubmit={handleVerify}>
                            <label className="field">
                                <span>Code from your app</span>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    pattern="\d{6}"
                                    className="input input-lg"
                                    placeholder="123 456"
                                    required
                                    autoFocus
                                    value={code}
                                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                />
                            </label>
                            <button
                                className="btn btn-accent auth-submit"
                                type="submit"
                                disabled={submitting || code.length !== 6 || !qrDataUrl}
                            >
                                {submitting ? 'Verifying…' : 'Turn on 2FA'}
                            </button>
                        </form>
                    )}
                </div>
            </section>
        </div>
    );
};

export default RushTwoFactorSetupPage;
